import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { PROJECT_MAX_DURATION, MEDIA_ITEM_MIN_DURATION } from 'consts'

import { pixelsToSeconds } from 'utils/timeline'
import { editorSelectors } from 'store/features/editor/selectors'
import { ITimelineItem } from 'types/timeline'
import useScrollWhenDragging from 'hooks/useScrollWhenDragging'
import { useTimelineContext } from 'context/TimelineContext'
import { sortArraysByKey } from 'store/features/projectData/utils'

const getDistanceToPrevItem = (
  items: ITimelineItem[],
  currentItemIndex: number
): number => {
  if (currentItemIndex === 0) {
    return 0
  }
  const currentItem = items[currentItemIndex]
  const prevItem = items[currentItemIndex - 1]
  const prevItemEnd = prevItem.startTime + prevItem.duration
  return currentItem.startTime - prevItemEnd
}

const getDistanceToNextItem = (
  items: ITimelineItem[],
  currentItemIndex: number
): number => {
  if (currentItemIndex === items.length - 1) {
    return 0
  }
  const currentItem = items[currentItemIndex]
  const nextItem = items[currentItemIndex + 1]
  const nextItemStart = nextItem.startTime
  return nextItemStart - (currentItem.startTime + currentItem.duration)
}

const useTimelineItemResize = (
  timelineItems: ITimelineItem[],
  isResizing: boolean
) => {
  const { scrollableContainerRef } = useTimelineContext()
  const [isScrollDisabled, setIsScrollDisabled] = useState(!isResizing)

  useEffect(() => {
    setIsScrollDisabled(!isResizing)
  }, [isResizing])

  useScrollWhenDragging({
    scrollableContainerRef,
    isDisabled: isScrollDisabled
  })

  const timelineItemsSorted = useMemo(
    () => sortArraysByKey('startTime', timelineItems),
    [timelineItems]
  )

  const [items, setItems] = useState(timelineItemsSorted)
  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )

  useEffect(() => {
    setItems(timelineItemsSorted)
  }, [timelineItemsSorted])

  const handleLeftResize = (id: string, newWidth: number) => {
    const newDuration = pixelsToSeconds(newWidth, timelineRulerSliceDuration)

    const currentItemIndex = timelineItemsSorted.findIndex(
      item => item.id === id
    )

    const { duration: initialDuration, trimStart: initialTrimStart } =
      timelineItemsSorted[currentItemIndex]

    const initialSpaceFromProjectStart = timelineItemsSorted[0].startTime
    const initialDistanceToPrevItem = getDistanceToPrevItem(
      timelineItemsSorted,
      currentItemIndex
    )
    const initialAvailableSpaceFromLeft =
      initialSpaceFromProjectStart + initialDistanceToPrevItem

    const durationDelta = newDuration - initialDuration

    const noTrimSpaceFromLeft =
      initialTrimStart !== undefined && initialTrimStart < durationDelta
    const noSpaceFromLeft = initialAvailableSpaceFromLeft < durationDelta
    const isMinDurationReached = newDuration <= MEDIA_ITEM_MIN_DURATION

    if (noTrimSpaceFromLeft || noSpaceFromLeft || isMinDurationReached) {
      setIsScrollDisabled(true)
      return
    }

    setIsScrollDisabled(false)

    setItems(items => {
      return items.map((_, idx) => {
        const initialItem = timelineItemsSorted[idx]
        const newItem = { ...initialItem }
        if (idx === currentItemIndex) {
          newItem.startTime = initialItem.startTime - durationDelta
          newItem.duration = initialItem.duration + durationDelta
          if (initialItem.trimStart !== undefined) {
            newItem.trimStart = initialItem.trimStart - durationDelta
          }
        }

        if (
          idx < currentItemIndex &&
          initialDistanceToPrevItem - durationDelta < 0
        ) {
          newItem.startTime = Math.min(
            initialItem.startTime,
            initialItem.startTime - (durationDelta - initialDistanceToPrevItem)
          )
        }

        return newItem
      })
    })
  }

  const handleRightResize = (id: string, newWidth: number) => {
    const newDuration = pixelsToSeconds(newWidth, timelineRulerSliceDuration)

    const currentItemIndex = timelineItemsSorted.findIndex(
      item => item.id === id
    )
    const lastItem = timelineItemsSorted[timelineItemsSorted.length - 1]
    const lastItemEnd = lastItem.startTime + lastItem.duration

    const {
      duration: initialDuration,
      trimStart: initialTrimStart,
      originalDuration
    } = timelineItemsSorted[currentItemIndex]
    const initialDistanceToNextItem = getDistanceToNextItem(
      timelineItemsSorted,
      currentItemIndex
    )

    const initialSpaceFromMaximumEnd = PROJECT_MAX_DURATION - lastItemEnd
    const initialAvailableSpaceFromRight =
      initialSpaceFromMaximumEnd + initialDistanceToNextItem

    const durationDelta = newDuration - initialDuration
    const noTrimSpaceFromRight =
      initialTrimStart !== undefined &&
      originalDuration !== undefined &&
      initialTrimStart + newDuration > originalDuration
    const noSpaceFromRight = initialAvailableSpaceFromRight < durationDelta
    const isMinDurationReached = newDuration <= MEDIA_ITEM_MIN_DURATION

    if (noTrimSpaceFromRight || noSpaceFromRight || isMinDurationReached) {
      setIsScrollDisabled(true)
      return
    }

    setIsScrollDisabled(false)

    setItems(items =>
      items.map((_, idx) => {
        const initialItem = timelineItemsSorted[idx]
        const newItem = { ...initialItem }

        if (idx === currentItemIndex) {
          newItem.duration = initialItem.duration + durationDelta
        }

        if (
          idx > currentItemIndex &&
          durationDelta > initialDistanceToNextItem
        ) {
          newItem.startTime = Math.max(
            initialItem.startTime,
            initialItem.startTime + (durationDelta - initialDistanceToNextItem)
          )
        }

        return newItem
      })
    )
  }

  return { items, handleLeftResize, handleRightResize }
}

export default useTimelineItemResize
